import { captureWebsiteDocument, savePdfResponse } from "utils/documentPdf";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMaterialUIController } from "context";
import AppHeader from "components/AppHeader";
import { DashboardNavigation } from "layouts/businessUserDashboard";
import {
  downloadQuotation,
  prepareQuotationEmail,
  prepareQuotationEmailDocument,
  PreviewQuotationDetails,
} from "utils/quotationAPI";
import { showResMsg } from "utils/generic";
import { appConstants } from "utils/appConstants";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { resolvePreviewReturn } from "utils/previewReturn";
import {
  parseAdditionalQuotationEmails,
  validateAdditionalQuotationEmails,
  validateQuotationCustomerField,
} from "utils/quotationCustomerValidation";
import QuotationSendDialog from "./QuotationSendDialog";
import DocumentSentSuccessDialog from "./DocumentSentSuccessDialog";
import DocumentPreviewSkeleton from "components/Skeleton/DocumentPreviewSkeleton";
import CustomerInvoicePreview from "layouts/Invoices/CustomerInvoicePreview";
import "./quotation.css";

const dateText = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

function PreviewDownloadQuotation() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: quotationID, quotationNumber: routeNumber } = useParams();
  const [controller] = useMaterialUIController();
  const [navOpen, setNavOpen] = useState(false);
  const previewSnapshot = location.state?.previewSnapshot;
  const [values, setValues] = useState(() => previewSnapshot?.values || {});
  const [items, setItems] = useState(() => previewSnapshot?.items || []);
  const [previewReady, setPreviewReady] = useState(Boolean(previewSnapshot));
  const [uploadedLogoFileName, setUploadedLogoFileName] = useState("");
  const [businessProfile, setBusinessProfile] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendSucceeded, setSendSucceeded] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendSubject, setSendSubject] = useState("");
  const [additionalRecipients, setAdditionalRecipients] = useState("");
  const [additionalRecipientsError, setAdditionalRecipientsError] =
    useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [sendCopy, setSendCopy] = useState(false);
  const [sendError, setSendError] = useState("");
  const sendInFlightRef = useRef(false);
  const sendSnapshotRef = useRef(null);
  const login = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS")) || {};
    } catch {
      return {};
    }
  }, []);
  const userName =
    `${login.firstName || ""} ${login.lastName || ""}`.trim() ||
    login.name ||
    "User";
  const organisation =
    login.businessName ||
    login.companyName ||
    login.organisationName ||
    login.organizationName ||
    "Invoice & Recover";
  const role = login.roleName || login.userType || "Owner";
  const previewReturn = resolvePreviewReturn(
    location,
    `/quotations/view/${quotationID}`,
    "Back to detail"
  );

  useEffect(() => {
    if (!login.businessUserID) return undefined;
    let active = true;
    axios
      .post(`${appConstants.baseURL}defaultSettings/fetchDefaultSetting`, {
        businessUserID: login.businessUserID,
      })
      .then((response) => {
        if (active) {
          setUploadedLogoFileName(
            response.data?.Result_Output?.defaultLogoFileName || ""
          );
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [login.businessUserID]);

  useEffect(() => {
    const userId = login._id || sessionStorage.getItem("USER_ID")?.replace(/["']/g, "");
    if (!userId) return undefined;
    let active = true;
    axios
      .post(
        `${appConstants.baseURL}user/fetchUserProfile`,
        { _id: userId },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` } }
      )
      .then(({ data }) => {
        if (!active) return;
        setBusinessProfile(
          Array.isArray(data?.Result_Output)
            ? data.Result_Output[0] || null
            : data?.Result_Output || null
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [login._id]);

  useEffect(() => {
    PreviewQuotationDetails(
      { quotationID },
      (data) => {
        const result = data?.Result_Output?.[0];
        if (!result)
          return showResMsg(
            "Quotation details could not be found",
            2000,
            false
          );
        setValues(result);
        setItems(result.itemsList || []);
        setPreviewReady(true);
      },
      dispatch
    );
  }, [dispatch, quotationID]);

  const quoteNumber = values.quotationNumber || routeNumber || "Auto generated";
  const currency = values.currencyValue || values.currencyCode || "AUD";
  const customer =
    values.customerCompanyName ||
    values.name ||
    values.contactName ||
    "Customer";
  const status = values.quotationStatus || "Draft";
  const logoFileName = values.defaultLogoFileName || uploadedLogoFileName;
  const businessLogo = logoFileName
    ? `${appConstants.baseURL}employee/viewFile?fileName=${encodeURIComponent(
        logoFileName
      )}`
    : "";
  const documentOrganisation =
    values.businessCompanyName || businessProfile?.companyName || organisation;
  const isApproved = String(status).trim().toLowerCase() === "approved";
  const recipient = String(
    values.recipientEmail || values.customerEmailAddress || ""
  ).trim();
  const recipientError = validateQuotationCustomerField("email", recipient);

  useEffect(() => {
    if (!sendOpen || !quotationID) return undefined;
    prepareQuotationEmail();
    return () => {
      sendSnapshotRef.current = null;
    };
  }, [sendOpen, quotationID]);

  const handleOpenSend = () => {
    if (sendInFlightRef.current || !values._id) return;
    if (
      !items.length ||
      items.some(
        (item) =>
          !String(item.itemName || item.itemDescription || "").trim() ||
          item.itemPrice === "" ||
          item.itemPrice == null ||
          !Number.isFinite(Number(item.itemPrice)) ||
          Number(item.itemPrice) < 0 ||
          !Number.isFinite(Number(item.itemQuantity)) ||
          Number(item.itemQuantity) <= 0
      )
    ) {
      showResMsg(
        "Please correct the quotation items in the editor before sending.",
        2500,
        false
      );
      return;
    }
    setSendSubject(
      `Quote ${quoteNumber} from ${organisation} · $${formatCurrencyAUD(
        parseFloat(values.finalAmount) || 0
      )} ${currency}`
    );
    setAdditionalRecipients("");
    setAdditionalRecipientsError("");
    setAttachPdf(true);
    setSendCopy(false);
    setSendError("");
    sendSnapshotRef.current = captureWebsiteDocument(
      "quotation",
      quotationID
    ).catch(() => null);
    setSendOpen(true);
  };

  const handleSendQuote = async () => {
    if (sendInFlightRef.current) return;
    const additionalError =
      validateAdditionalQuotationEmails(additionalRecipients);
    setAdditionalRecipientsError(additionalError);
    if (recipientError || additionalError) return;
    sendInFlightRef.current = true;
    setSendingEmail(true);
    setSendError("");
    try {
      const documentPayload = await prepareQuotationEmailDocument(
        quotationID,
        sendSnapshotRef.current
      );
      const { data } = await axios.post(
        `${appConstants.baseURL}quotation/emailReminderQuotation`,
        {
          quotationID,
          userID: login._id,
          businessUserID: login.businessUserID,
          recipientEmail: recipient,
          additionalRecipientEmails:
            parseAdditionalQuotationEmails(additionalRecipients),
          emailSubject: sendSubject.trim(),
          attachPdf,
          sendCopy,
          ...documentPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}`,
          },
          timeout: 60000,
        }
      );
      if (!data?.Result_Status)
        throw new Error(
          data?.Result_Message ||
            "The quotation could not be sent. Please try again."
        );
      setSendOpen(false);
      setSendSucceeded(true);
    } catch (error) {
      setSendError(
        error.response?.data?.Result_Message ||
          error.message ||
          "The quotation could not be sent. Please try again."
      );
    } finally {
      sendInFlightRef.current = false;
      setSendingEmail(false);
    }
  };

  const handleDownload = () =>
    downloadQuotation(
      { _id: quotationID },
      (response) => savePdfResponse(response, `quotation_${quoteNumber}.pdf`),
      dispatch
    );

  return (
    <div
      className={`dashboard-shell quotation-preview-shell${
        controller.darkMode ? " dark" : ""
      }`}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath="/quotations"
        organisation={organisation}
        role={role}
      />
      <div className="dashboard-main quotation-preview-main">
        <AppHeader onOpenNav={() => setNavOpen(true)} userName={userName} />
        <main>
          {!previewReady ? (
            <DocumentPreviewSkeleton
              variant="invoice"
              label="Loading quotation preview"
            />
          ) : (
            <CustomerInvoicePreview
              documentType="quote"
              organisation={documentOrganisation}
              businessLogo={businessLogo}
              invoiceNumber={quoteNumber}
              status={status}
              customerName={customer}
              customerDetails={{
                emailAddress: recipient,
                streetAddress: values.customerStreetAddress,
              }}
              invoiceDate={values.quotationDate}
              dueDate={values.validUptoDate}
              dueUpto={values.validUpto}
              currency={currency}
              gstType={values.gstType}
            items={items}
              purchaseOrderNumber={values.purchaseOrderNumber}
              scopeNotes={values.paymentInstruction}
              invoiceNotes={values.terms}
              paymentInstructions=""
              bankAccountName={values.bankAccountName || businessProfile?.bankAccountName}
              bankBsbCode={values.bankBsbCode || businessProfile?.bankBsbCode}
              bankAccountNumber={values.bankAccountNumber || businessProfile?.bankAccountNumber}
              subtotal={values.totalAmount}
              totalDiscounts={values.totalDiscountAmount}
              totalGST={values.totalGSTAmount}
              grandTotal={values.finalAmount}
              onBack={() => navigate(previewReturn.path)}
              backLabel={previewReturn.label}
              onDownload={handleDownload}
              onSend={isApproved ? undefined : handleOpenSend}
              canSend={Boolean(values._id)}
              isSaved={Boolean(values._id)}
              isSending={sendingEmail}
              darkMode={controller.darkMode}
            />
          )}
        </main>
      </div>
      {sendOpen && (
        <div
          className={`quotation-editor-shell${
            controller.darkMode ? " dark" : ""
          }`}
          style={{ display: "contents" }}
        >
          <QuotationSendDialog
            id="preview-send-recipient"
            values={{ ...values, name: customer }}
            organisation={organisation}
            currencyCode={currency}
            recipient={recipient}
            recipientError={recipientError}
            additionalRecipientsError={additionalRecipientsError}
            onRecipientsChange={(emails) => {
              setAdditionalRecipients(emails);
              setSendError("");
            }}
            onRecipientsError={setAdditionalRecipientsError}
            subject={sendSubject}
            onSubjectChange={setSendSubject}
            attachPdf={attachPdf}
            onAttachPdfChange={setAttachPdf}
            sendCopy={sendCopy}
            onSendCopyChange={setSendCopy}
            busy={sendingEmail}
            sendingEmail={sendingEmail}
            error={sendError}
            onClose={() => {
              if (!sendInFlightRef.current) setSendOpen(false);
            }}
            onEditRecipient={() => navigate(`/quotations/edit/${quotationID}`)}
            onSend={handleSendQuote}
          />
        </div>
      )}
      <DocumentSentSuccessDialog
        open={sendSucceeded}
        documentType="quote"
        documentNumber={quoteNumber}
        recipient={recipient}
        onClose={() => setSendSucceeded(false)}
        onViewDetails={() => navigate(`/quotations/view/${quotationID}`)}
      />
    </div>
  );
}

export default PreviewDownloadQuotation;
